export type CsvValue = string | number | boolean | null | undefined;

const FORMULA_PREFIX = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;

// A spreadsheet runs a cell that starts with = + - @ (or a tab or carriage
// return before one) as a formula, so exported text could carry one in from
// an attached file or an event property. A leading ' keeps it as text. Plain
// numbers such as "-12.5" are left alone.
function neutralizeFormula(value: CsvValue): string {
  const text = String(value ?? "");
  if (typeof value !== "string") return text;
  return FORMULA_PREFIX.test(text) && !PLAIN_NUMBER.test(text)
    ? `'${text}`
    : text;
}

export function buildCsv(rows: CsvValue[][]): string {
  const escapeCsv = (value: CsvValue) =>
    `"${neutralizeFormula(value).replace(/"/g, '""')}"`;

  return rows.map((row) => row.map(escapeCsv).join(",")).join("\n");
}

export function downloadCsv({
  filename,
  header,
  rows,
  bom = false,
}: {
  filename: string;
  header: CsvValue[];
  rows: CsvValue[][];
  // Prefixes the file with a UTF-8 BOM so Excel recognizes the encoding and
  // renders non-ASCII characters correctly when the file is double-clicked
  // open instead of imported through Excel's encoding-aware import wizard.
  bom?: boolean;
}): void {
  const csv = buildCsv([header, ...rows]);
  const blob = new Blob([bom ? `\uFEFF${csv}` : csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
